import { compareDates } from '../utils-common'
import AuthService from '../AuthService'

/* global AZURE_URI, LOCALE */
class AvailabilityCalendar {
  constructor () {
    this.availability = []
    this.loading = false
    this.loadingHash = null
  }

  get hasAvailableDates () {
    return this.availability.find(date => date.rateFrom != null)
  }

  get disabledDates () {
    return this.availability
      .filter((date, index) => !(date.rateFrom != null) && !(this.availability[index - 1]?.rateFrom != null))
      .map(obj => obj.date)
  }

  get checkoutOnlyDates () {
    return this.availability
      .filter((date, index) => (!(date.rateFrom != null) && this.availability[index - 1]?.rateFrom != null))
      .map(obj => obj.date)
  }

  get minAvailableDate () {
    return this.availability.find(day => day.rateFrom != null && day.date.toISOString().split('T')[0] >= new Date().toISOString().split('T')[0])
  }

  get maxAvailableDate () {
    return this.availability.length === 0 ? null : this.availability[this.availability.length - 1]
  }

  isDateAvailable (date) {
    if (date != null) {
      const selectedDateAvailability = this.availability.find(day => day.date != null && compareDates(day.date, date))
      return selectedDateAvailability != null && selectedDateAvailability.rateFrom != null
    }
    return false
  }

  isDateRangeAvailable (date, nights) {
    const startDate = new Date(new Date(date).setHours(0))
    const endDate = new Date(new Date(startDate))
    endDate.setDate(endDate.getDate() + nights)

    return this.availability
      .filter(({ date, rateFrom }) => date.getTime() >= startDate.getTime() && date.getTime() < endDate.getTime() && rateFrom != null)
      .length === nights
  }

  getFirstAvailableDateRange (nights) {
    const d = new Date(this.minAvailableDate?.date)
    const max = new Date(this.maxAvailableDate?.date)
    while (d.getTime() <= max.getTime()) {
      if (this.isDateRangeAvailable(d, nights)) return d
      d.setDate(d.getDate() + 1)
    }
    return null
  }

  reset () {
    this.availability = []
    this.loading = false
    this.loadingHash = null
  }

  async loadAvailability ({ propertyIds, unitId, accessCode, specialOffer }) {
    const loadingHash = JSON.stringify({ propertyIds, unitId, accessCode, specialOffer })
    if (this.loadingHash === loadingHash) {
      return
    }

    this.loading = true
    this.loadingHash = loadingHash

    try {
      const validPropertyIds = (propertyIds || []).filter(id => id.length === 32)
      if (validPropertyIds.length === 0) {
        this.availability = []
        this.loading = false
        return
      }

      const params = {
        id: validPropertyIds.join(';'),
        accessCode: accessCode || '',
        unitId,
        locale: LOCALE,
      }
      if (AuthService.isMaiFamily) {
        params.isMaiFamily = true
      }
      const queryParamsString = Object.keys(params)
        .filter(key => params[key] != null)
        .map(key => `${key}=${params[key]}`)
        .join('&')

      this.availability = []

      const response = await fetch(`${AZURE_URI}GetAvailabilityCalendar?${queryParamsString}`)
      if (!response.ok) {
        throw new Error(response.statusText)
      }

      let responseData = await response.json()

      if (specialOffer && (specialOffer.periods.length || specialOffer.days)) {
        responseData = responseData.map(day => {
          const matchesPeriods =
            specialOffer.periods.length === 0 ||
            specialOffer.periods.some(
              period => day.date >= period.start_date.split('T')[0] && day.date <= period.end_date.split('T')[0])

          const matchesDays =
            !specialOffer.days ||
            specialOffer.days.length < 7 ||
            !specialOffer.days.some(day => day === 1) ||
            specialOffer.days[(new Date(day.date).getDay() + 6) % 7]

          if (matchesPeriods && matchesDays) {
            return day
          }
          return {
            date: day.date,
          }
        })
      }

      this.availability = responseData.map(obj => ({
        ...obj,
        date: new Date(new Date(obj.date)),
      }))
      this.loading = false
    } catch (e) {
      this.loading = false
      throw new Error(e)
    }
  }
}

export { AvailabilityCalendar }
